/**
 * Scroll depth observer — fires once per depth threshold.
 * Single passive scroll listener, removes itself when all thresholds are hit.
 */

import type { EventMessage } from "../messages";

type OnEvent = (msg: EventMessage) => void;

interface ScrollDepthTriggerDef {
  id: string;
  depth: number;
}

export function setupScrollObservers(
  triggers: ScrollDepthTriggerDef[],
  onEvent: OnEvent,
): () => void {
  // Sort ascending by depth so we can check lowest thresholds first
  const pending = [...triggers].sort((a, b) => a.depth - b.depth);

  function getScrollPercent(): number {
    const scrollHeight = document.documentElement.scrollHeight;
    const viewportHeight = window.innerHeight;
    if (scrollHeight <= viewportHeight) return 100;
    return ((window.scrollY + viewportHeight) / scrollHeight) * 100;
  }

  function onScroll() {
    const pct = getScrollPercent();
    while (pending.length > 0 && pct >= pending[0].depth) {
      const trigger = pending.shift()!;
      onEvent({
        type: "event",
        event: "scroll_depth",
        triggerId: trigger.id,
        depth: trigger.depth,
        url: window.location.href,
      });
    }
    if (pending.length === 0) {
      window.removeEventListener("scroll", onScroll);
    }
  }

  window.addEventListener("scroll", onScroll, { passive: true });

  // Check initial scroll position
  onScroll();

  return () => {
    window.removeEventListener("scroll", onScroll);
    pending.length = 0;
  };
}
